import { Box, Flex, FlexProps, Text } from '@chakra-ui/react'
import { MinusCircle, Voicemail } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { Size } from '~/types/ThemeTypes'

import {
  formatDateString,
  formatTimeString,
  formatTimezoneAbbr,
} from '@mangovoice/core'
import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { VoicemailToolTip } from '../Calls/CallDetails/VoicemailToolTip'
import { ContactCell } from '../Calls/cells/ContactCell'
import { DateTimeCell } from '../Calls/cells/DateTimeCell'
import { AssignedUserDropdown } from './components/AssignedUserDropdown'
import { FollowUpStatusDropdown } from './components/FollowUpStatusDropdown'
import { MissedCallActions } from './components/MissedCallActions'
import { TypeBadge } from './components/TypeBadge'
import { useMissedCallsListItemStyles } from './componentStyles/MissedCallsListItem.style'
import { useMissedCallsTableStyles } from './componentStyles/MissedCallsTable.style'
import { MissedCallsTableColumn } from './data/MissedCallsTableData'
import { MissedCall } from './types/MissedCall'

interface MissedCallListItemProps extends FlexProps {
  missedCall: MissedCall
  isSelected?: boolean
  tableSize: Size
  isFirst?: boolean
  onFollowUpStatusChanged?: (missedCallId: number, newStatus: string) => void
  users?: { id: number; full_name?: string | null }[]
  isUsersLoading?: boolean
  onAssigneeChanged?: (missedCallId: number, userId: number | null) => void
}

export const MissedCallListItem = ({
  missedCall,
  isSelected,
  tableSize,
  isFirst,
  onFollowUpStatusChanged,
  users,
  isUsersLoading,
  onAssigneeChanged,
}: MissedCallListItemProps) => {
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)
  const { pbxId } = useUserState()
  const styles = useMissedCallsListItemStyles({ size: tableSize })
  const tableStyles = useMissedCallsTableStyles({ size: tableSize })

  const isAssigneeSelected =
    missedCall.assigned_to_user_id !== null &&
    missedCall.assigned_to_user_id !== undefined
  const resolvedFollowUpStatus = isAssigneeSelected
    ? (missedCall.follow_up_status ?? 'Open')
    : 'Open'

  const isVoicemail = (missedCall.type ?? '').toLowerCase() === 'voicemail'
  const isAbandoned = (missedCall.type ?? '').toLowerCase() === 'abandoned'

  const handleHoverStart = () => {
    setIsHovered(true)
  }
  const handleHoverEnd = () => {
    setIsHovered(false)
  }

  // Format date and time - use formatTimeString/formatDateString directly
  const date = formatDateString(missedCall.created_at)
  const time = formatTimeString(missedCall.created_at)

  // Format follow-up time and date
  const followUpTime = missedCall.follow_up_time_utc
    ? formatTimeString(missedCall.follow_up_time_utc)
    : null
  const followUpDate = missedCall.follow_up_time_utc
    ? formatDateString(missedCall.follow_up_time_utc)
    : null

  // Get timezone abbreviation for display (browser's local timezone)
  const timezoneAbbr = missedCall.follow_up_time_utc
    ? formatTimezoneAbbr(missedCall.follow_up_time_utc)
    : ''

  return (
    <Flex
      css={styles.container}
      cursor="default"
      aria-selected={isSelected}
      data-first={isFirst}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
      position="relative"
      zIndex={isHovered ? 10 : 1}
    >
      <Flex css={styles.rowWrapper}>
        {/* Date & Time Column */}
        <DateTimeCell
          columnStyles={tableStyles[MissedCallsTableColumn.DateTime]}
          date={date}
          time={time}
        />

        {/* From Column */}
        <ContactCell
          columnStyles={tableStyles[MissedCallsTableColumn.From]}
          title={missedCall.from_caller_id ?? missedCall.from_number ?? ''}
          subtitle={
            missedCall.from_caller_id ? (missedCall.from_number ?? '') : ''
          }
          contact={undefined}
          number={missedCall.from_number}
          isExtension={false}
          isHovered={isHovered}
          showMenu={false}
        />

        {/* To Column */}
        <ContactCell
          columnStyles={tableStyles[MissedCallsTableColumn.To]}
          title={missedCall.to_caller_id ?? missedCall.to_number ?? ''}
          subtitle={missedCall.to_caller_id ? (missedCall.to_number ?? '') : ''}
          contact={undefined}
          number={missedCall.to_number}
          isExtension={false}
          isHovered={isHovered}
          showMenu={false}
        />

        {/* Type Column */}
        <Flex
          css={{
            ...styles.typeColumn,
            ...tableStyles[MissedCallsTableColumn.Type],
            alignItems: 'center',
            pl: '1rem',
          }}
        >
          <Flex css={styles.titleContainer}>
            {isVoicemail ? (
              <TypeBadge
                Icon={Voicemail}
                iconColor={asChakraVariable('purple.fg')}
                alt={t('missedCalls.type.voicemailAlt', 'Voicemail')}
                bg="purple.subtle"
                popoverContent={
                  missedCall.voicemail_id && pbxId ? (
                    <VoicemailToolTip
                      voicemailId={missedCall.voicemail_id}
                      pbxId={pbxId}
                      callId={missedCall.id}
                    />
                  ) : undefined
                }
              />
            ) : isAbandoned ? (
              <TypeBadge
                Icon={MinusCircle}
                iconColor={asChakraVariable('red.solid')}
                alt={t('missedCalls.type.abandonedAlt', 'Abandoned')}
                bg="red.subtle"
                popoverContent={
                  <Flex flexDirection="column">
                    <Flex
                      color="fg.default"
                      {...textStyles[textStyleKeys.Body1_Bold]}
                    >
                      {t('missedCalls.type.abandonedTitle', 'Abandoned')}
                    </Flex>
                    <Flex
                      color="fg.default"
                      {...textStyles[textStyleKeys.Body1]}
                      mt="0.5rem"
                    >
                      {t(
                        'missedCalls.type.abandonedDescription',
                        'The call was not answered, and the customer did not leave a voicemail',
                      )}
                    </Flex>
                  </Flex>
                }
              />
            ) : (
              <Text css={styles.titleText}>{missedCall.type}</Text>
            )}
          </Flex>
        </Flex>

        {/* Follow up Time Column */}
        <Flex
          css={{
            ...styles.followUpTimeColumn,
            ...tableStyles[MissedCallsTableColumn.FollowUpTime],
          }}
        >
          {followUpTime && followUpDate ? (
            <Flex
              css={{
                ...tableStyles[MissedCallsTableColumn.FollowUpTime],
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pr: '1rem',
                minWidth: 0,
              }}
            >
              <Flex alignItems="center" gap="0.25rem" minWidth={0}>
                <Text
                  {...textStyles[textStyleKeys.Body1]}
                  color="fg.default"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {followUpTime}
                </Text>
                {timezoneAbbr && (
                  <Text
                    {...textStyles[textStyleKeys.Body3]}
                    color="fg.muted"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {timezoneAbbr}
                  </Text>
                )}
              </Flex>
              <Text
                {...textStyles[textStyleKeys.Body3]}
                color="fg.muted"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {followUpDate}
              </Text>
            </Flex>
          ) : (
            <Flex css={{ ...styles.titleContainer, ml: '-0.25rem' }}>
              <Text css={styles.titleText}>--:--</Text>
            </Flex>
          )}
        </Flex>

        {/* Follow up Status Column */}
        <Flex
          css={{
            ...styles.followUpStatusColumn,
            ...tableStyles[MissedCallsTableColumn.FollowUpStatus],
          }}
          overflow="visible"
        >
          <Flex css={{ ...styles.titleContainer, pr: 0 }} overflow="visible">
            {isAssigneeSelected ? (
              <Box width="90%">
                <FollowUpStatusDropdown
                  value={resolvedFollowUpStatus}
                  onChange={(newValue) => {
                    onFollowUpStatusChanged?.(missedCall.id, newValue)
                  }}
                  isDisabled={false}
                />
              </Box>
            ) : (
              <Text css={styles.titleText}>-</Text>
            )}
          </Flex>
        </Flex>

        {/* Assigned to Column */}
        <Flex
          css={{
            ...styles.assignedToColumn,
            ...tableStyles[MissedCallsTableColumn.AssignedTo],
          }}
          overflow="visible"
        >
          <Flex css={{ ...styles.titleContainer, pr: 0 }} overflow="visible">
            <Box width="90%">
              <AssignedUserDropdown
                users={users}
                selectedUserId={missedCall.assigned_to_user_id ?? null}
                onChange={(userId) =>
                  onAssigneeChanged?.(missedCall.id, userId)
                }
                isLoading={isUsersLoading}
              />
            </Box>
          </Flex>
        </Flex>

        {/* Action Column */}
        <Flex
          css={{
            ...styles.actionColumn,
            ...tableStyles[MissedCallsTableColumn.Action],
          }}
        >
          <Flex css={styles.titleContainer}>
            <MissedCallActions
              number={missedCall.from_number}
              isDisabled={!isAssigneeSelected}
              missedCallId={missedCall.id}
              onFollowUpStatusChanged={onFollowUpStatusChanged}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
